import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import parse, { domToReact, HTMLReactParserOptions, Element } from 'html-react-parser';
import { SectionContent } from '../../Services/UserProjectService';
import { useContentVariation, ProjectTreatment } from '../../hooks/useContentVariation';

interface TextSimpleViewerProps {
  content: SectionContent;
  project?: ProjectTreatment;
  navigationDirection?: 'forward' | 'backward';
}

/**
 * Helper function to parse CSS style string to React style object
 * Converts "text-align:center; font-weight:bold" to {textAlign: 'center', fontWeight: 'bold'}
 */
const parseInlineStyle = (styleString: string): React.CSSProperties => {
  if (!styleString) return {};

  const styles: any = {};
  const declarations = styleString.split(';').filter(s => s.trim());

  declarations.forEach(declaration => {
    const [property, value] = declaration.split(':').map(s => s.trim());
    if (property && value) {
      // Convert kebab-case to camelCase (e.g., text-align -> textAlign)
      const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      styles[camelProperty] = value;
    }
  });

  return styles;
};

const TextSimpleViewer: React.FC<TextSimpleViewerProps> = ({ content, project, navigationDirection = 'forward' }) => {
  const { getVariationText } = useContentVariation(project);
  const textContent = getVariationText(content);
  const textLength = textContent.length;

  // Create a unique key based on content to force re-render
  const contentKey = useMemo(() => content.id || textContent.substring(0, 50), [content.id, textContent]);

  // Parse HTML and wrap paragraphs with motion
  const parsedContent = useMemo(() => {
    let elementIndex = 0;

    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        const node = domNode as any;

        // Check if it's a tag element
        if (node.type === 'tag') {
          // Animate paragraphs
          if (node.name === 'p') {
            const index = elementIndex++;

            // Extract original attributes
            const originalAttribs = node.attribs || {};

            // Parse inline style if it exists
            const inlineStyles = originalAttribs.style
              ? parseInlineStyle(originalAttribs.style)
              : {};

            // Merge original styles with animation styles (original styles first, then defaults)
            const mergedStyles = {
              ...inlineStyles,  // Original styles from HTML
              fontSize: '1.1rem',
              lineHeight: '2.2',
              fontWeight: '300'
            };

            return (
              <motion.p
                key={`${contentKey}-p-${index}`}
                className={originalAttribs.class || originalAttribs.className}
                initial={{ opacity: 0}}
                animate={{ opacity: 1 }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }}
                style={mergedStyles}
              >
                {domToReact(node.children as any, options)}
              </motion.p>
            );
          }

          // Animate lists
          if (node.name === 'ul' || node.name === 'ol') {
            const index = elementIndex++;
            const Tag = node.name as 'ul' | 'ol';

            // Extract original attributes
            const originalAttribs = node.attribs || {};

            // Parse inline style if it exists
            const inlineStyles = originalAttribs.style
              ? parseInlineStyle(originalAttribs.style)
              : {};

            // Merge original styles with defaults
            const mergedStyles = {
              ...inlineStyles,
              fontSize: '1.1rem',
              lineHeight: '2.2',
              fontWeight: '300'
            };

            return (
              <motion.div
                key={`${contentKey}-list-${index}`}
                initial={{ opacity: 0  }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }}
              >
                <Tag
                  className={originalAttribs.class || originalAttribs.className}
                  style={mergedStyles}
                >
                  {domToReact(node.children as any, options)}
                </Tag>
              </motion.div>
            );
          }
        }
      }
    };

    return parse(textContent, options);
  }, [textContent, textLength, contentKey]);

  return (
    <div
      className="text-content"
      style={{
        maxWidth: '100%',
        width: '100%',
        textAlign: 'left',
        padding: '0 2rem'
      }}
    >
      {parsedContent}
    </div>
  );
};

export default TextSimpleViewer;